/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useMemo } from 'react';
import { getGlobalConfigUrl } from '@jira/servicedesk__insight-common-utils-insight-urls/src/getLandingPageUrl.tsx';
import {
	FIXED_ITEM_ACTION,
	FIXED_ITEM_BUTTON,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import {
	ASSETS_CONFIG,
	MEATBALL_DROPDOWN,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-id.tsx';
import {
	LEVEL_ONE,
	LEVEL_TWO,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { SECTION_ITEM_ASSETS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { MENU_ID_ASSETS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	type OnCloseProps,
	L1_MENU_ID,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useStopPropagation } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-stop-propagation/index.tsx';
import { HideL1MenuItemSection } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/hide-l1-menu-item-action/index.tsx';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import { token } from '@atlaskit/tokens';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import Section from '@atlaskit/menu/section';
import LinkItem from '@atlaskit/menu/link-item';
import Settings from '@atlaskit/icon/core/settings';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

const jsmAssetsGlobalConfiguration = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-assets.assets-menu.section-actions.jsm-assets-global-configuration',

	defaultMessage: 'General configuration',
	description: 'Assets dropdown item to link customer to global configuration page.',
});

const jsmAssetsSettingsIcon = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-assets.assets-menu.section-actions.jsm-assets-settings-icon',

	defaultMessage: 'Settings',
	description: 'Settings icon in the Assets global configuration dropdown item',
});

type SectionActionsProps = {
	isInsightAdministrator: boolean | undefined;
} & OnCloseProps;

export function SectionActions({
	onClose,
	isInsightAdministrator = false,
}: SectionActionsProps): React.JSX.Element {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onOpen = () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'button',
				action: 'clicked',
			}),
			MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_ASSETS],
			{
				level: LEVEL_ONE,
				componentType: FIXED_ITEM_BUTTON,
				itemId: MEATBALL_DROPDOWN,
			},
		);
	};

	return (
		<MoreNavMenuButton
			MenuItems={(props) => (
				<MoreMenuItems {...props} isInsightAdministrator={isInsightAdministrator} />
			)}
			onClose={onClose}
			onOpen={() => onOpen()}
		/>
	);
}

type MoreMenuItemsProps = {
	isInsightAdministrator: boolean | undefined;
} & SectionActionsProps;

function MoreMenuItems({ onClose, isInsightAdministrator = false }: MoreMenuItemsProps) {
	const { formatMessage } = useIntl();
	const configLink = useMemo(() => getGlobalConfigUrl(), []);
	const onCloseWithStopPropagation = useStopPropagation(onClose);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'dropdownItem',
			}),
			MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_ASSETS],
			{
				level: LEVEL_TWO,
				componentType: FIXED_ITEM_ACTION,
				parentItemId: SECTION_ITEM_ASSETS,
				itemId: ASSETS_CONFIG,
			},
		);

		onCloseWithStopPropagation(e);
	};

	return (
		<>
			{isInsightAdministrator ? (
				<Section>
					<LinkItem href={configLink} onClick={(e) => onClick(e)}>
						<Inline alignBlock="center" space="space.050">
							<Settings
								label={formatMessage(jsmAssetsSettingsIcon)}
								spacing="spacious"
								color={token('color.icon')}
							/>
							{formatMessage(jsmAssetsGlobalConfiguration)}
						</Inline>
					</LinkItem>
				</Section>
			) : null}
			{!fg('navx-global-side-nav') && <HideL1MenuItemSection menuId={L1_MENU_ID.ASSETS} />}
		</>
	);
}
